import type {
  HostInsights,
  IpInsights,
  TechExposureResponse,
} from '../../app/modules/types/assetInsights'

/**
 * Fixtures for the `assetDetails` / `ipDetails` guided tours — seeded into the
 * insights + tech-exposure query keys by `seedAssetDetails` / `seedIpDetails`
 * in `../index.tsx`.
 *
 * Dates are computed relative to `Date.now()`, not hardcoded, because the
 * Scans-tab lane chart clips to a 90-day window measured from "now" — a fixed
 * 2024 date would eventually fall outside that window and the tour would show
 * an empty chart instead of the real page it is meant to explain.
 *
 * Every block below is deliberately non-trivial — an assessed grade with a
 * real 30-day delta, a non-zero severity spread, open findings in all three
 * age buckets, a `do_next` row with no `representative_finding_id`, one
 * `SKIP` and one never-reached (`null`) reachability layer, and an active
 * schedule — so a tour reader lands on a page that looks like a real asset,
 * not the ~87% "never scanned" case the empty states already cover.
 */

const DAY = 86_400_000
const daysAgo = (n: number) => new Date(Date.now() - n * DAY).toISOString()

/* ---------------------------------------------------------------- host ---- */

export const MOCK_HOST_INSIGHTS: HostInsights = {
  grade: { current: 'B', previous_30d: 'D', delta: 'better', assessed: true },
  severity: { critical: 3, high: 6, medium: 10, low: 12, info: 6, total: 37 },
  changed_30d: {
    opened: 9,
    closed: 14,
    closed_by_reason: { fixed: 8, not_detected: 3, false_positive: 2, accepted_risk: 1 },
  },
  aging: {
    buckets: { within_30d: 9, days_31_to_365: 22, over_365d: 6 },
    oldest_open: { finding_id: 'mock-finding-2001', days: 410, severity: 2, severity_name: 'LOW' },
    median_age_days: 58,
  },
  do_next: [
    {
      vuln_id: 'mock-vuln-1001',
      representative_finding_id: 'mock-finding-1001',
      action_label: 'Upgrade OpenSSH to close 3 findings on this host',
      label_source: 'remediation',
      severity: 4,
      severity_name: 'HIGH',
      closes: 3,
      locations: ['22/tcp'],
    },
    {
      // Predates finding grouping — renders in full, but is not a link.
      vuln_id: 'mock-vuln-1002',
      representative_finding_id: null,
      action_label: 'Rotate the expired TLS certificate on 443/tcp',
      label_source: 'title',
      severity: 5,
      severity_name: 'CRITICAL',
      closes: 1,
      locations: ['443/tcp'],
    },
    {
      vuln_id: 'mock-vuln-1003',
      representative_finding_id: 'mock-finding-1003',
      action_label: 'Patch the outdated WordPress core to close 2 findings',
      label_source: 'remediation',
      severity: 3,
      severity_name: 'MEDIUM',
      closes: 2,
      locations: [],
    },
  ],
  coverage: {
    dimensions: [
      { name: 'vulndiscovered', last_scan_at: daysAgo(2), days_ago: 2 },
      { name: 'port', last_scan_at: daysAgo(2), days_ago: 2 },
      { name: 'tech', last_scan_at: daysAgo(9), days_ago: 9 },
      { name: 'ssl', last_scan_at: daysAgo(9), days_ago: 9 },
      { name: 'dnsrecord', last_scan_at: daysAgo(30), days_ago: 30 },
      { name: 'reachability', last_scan_at: daysAgo(0), days_ago: 0 },
      { name: 'host', last_scan_at: daysAgo(120), days_ago: 120 },
      // An unrecognised machine key — renders as "some other dimension", never a crash.
      { name: 'legacy_probe', last_scan_at: daysAgo(400), days_ago: 400 },
    ],
    last_successful_scan_at: daysAgo(2),
    vuln_assessed: true,
  },
  reachability: {
    layers: { dns: 'OK', icmp: 'SKIP', tcp: 'OK', service: null, web: 'FAILED' },
    probed_at: daysAgo(0),
  },
  scan_activity: {
    scans: [
      { scan_id: 'mock-scan-h1', start_time: daysAgo(2), status: 'completed', components: ['port', 'vulndiscovered', 'tech'] },
      { scan_id: 'mock-scan-h2', start_time: daysAgo(9), status: 'completed', components: ['dns', 'ssl'] },
      { scan_id: 'mock-scan-h3', start_time: daysAgo(16), status: 'partial', components: ['port'] },
      // A scan with no attributed component still happened to this asset.
      { scan_id: 'mock-scan-h4', start_time: daysAgo(30), status: 'failed', components: [] },
      { scan_id: 'mock-scan-h5', start_time: daysAgo(55), status: 'completed', components: ['unattributed'] },
    ],
    typical_gap_days: 10,
    days_since_last_successful: 2,
  },
  schedule: {
    exists: true,
    rate: 'weekly',
    is_active: true,
    deactivate_reason: null,
    recent_failed_runs: 1,
  },
  neighbors: {
    ips: [
      { id: 'mock-ip-1', ip: '93.184.216.34', grade: 'B', asn_name: 'MCI Communications Services', loc: 'US' },
      { id: 'mock-ip-2', ip: '93.184.216.35', grade: null, asn_name: null, loc: 'US' },
      { id: 'mock-ip-3', ip: '2606:2800:220:1::1', grade: 'A', asn_name: 'Edgecast Inc.', loc: 'DE' },
    ],
  },
}

/* ------------------------------------------------------------------ ip ---- */

export const MOCK_IP_INSIGHTS: IpInsights = {
  grade: { current: 'C', previous_30d: 'C', delta: 'same', assessed: true },
  severity: { critical: 1, high: 3, medium: 5, low: 8, info: 4, total: 21 },
  changed_30d: {
    opened: 4,
    closed: 2,
    closed_by_reason: { fixed: 1, not_detected: 0, false_positive: 1, accepted_risk: 0 },
  },
  aging: {
    buckets: { within_30d: 4, days_31_to_365: 12, over_365d: 5 },
    oldest_open: { finding_id: 'mock-finding-3001', days: 520, severity: 4, severity_name: 'HIGH' },
    median_age_days: 75,
  },
  do_next: [
    {
      vuln_id: 'mock-vuln-2001',
      representative_finding_id: 'mock-finding-2101',
      action_label: 'Close the exposed Redis instance to remove 2 findings',
      label_source: 'remediation',
      severity: 5,
      severity_name: 'CRITICAL',
      closes: 2,
      locations: ['6379/tcp'],
    },
    {
      vuln_id: 'mock-vuln-2002',
      representative_finding_id: null,
      action_label: 'Disable the outdated TLS 1.0 cipher suite',
      label_source: 'title',
      severity: 3,
      severity_name: 'MEDIUM',
      closes: 1,
      locations: [],
    },
  ],
  coverage: {
    dimensions: [
      { name: 'port', last_scan_at: daysAgo(1), days_ago: 1 },
      { name: 'tech', last_scan_at: daysAgo(6), days_ago: 6 },
      { name: 'ssl', last_scan_at: daysAgo(6), days_ago: 6 },
      { name: 'reachability', last_scan_at: daysAgo(0), days_ago: 0 },
      { name: 'ipaddress', last_scan_at: daysAgo(19), days_ago: 19 },
      { name: 'dns', last_scan_at: daysAgo(200), days_ago: 200 },
    ],
    last_successful_scan_at: daysAgo(1),
    vuln_assessed: true,
  },
  reachability: {
    layers: { dns: 'OK', icmp: null, tcp: 'OK', service: 'SKIP', web: 'OK' },
    probed_at: daysAgo(0),
  },
  scan_activity: {
    scans: [
      { scan_id: 'mock-scan-i1', start_time: daysAgo(1), status: 'completed', components: ['port', 'ssl'] },
      { scan_id: 'mock-scan-i2', start_time: daysAgo(6), status: 'completed', components: ['tech'] },
      { scan_id: 'mock-scan-i3', start_time: daysAgo(19), status: 'partial', components: ['reachability'] },
      { scan_id: 'mock-scan-i4', start_time: daysAgo(38), status: 'completed', components: [] },
    ],
    typical_gap_days: 9,
    days_since_last_successful: 1,
  },
  schedule: {
    exists: true,
    rate: 'monthly',
    is_active: true,
    deactivate_reason: null,
    recent_failed_runs: 0,
  },
  neighbors: {
    host_count: 47,
    truncated: true,
    hosts: [
      { id: 'mock-asset-1', title: 'example.com', grade: 'B' },
      { id: 'mock-asset-4', title: 'shop.example.com', grade: 'C' },
      { id: 'mock-asset-5', title: 'cdn.example.com', grade: null },
      { id: 'mock-asset-6', title: 'api.example.com', grade: 'A' },
      { id: 'mock-asset-7', title: 'status.example.com', grade: 'D' },
    ],
  },
}

/* ----------------------------------------------------------- tech-exposure */

export const MOCK_TECH_EXPOSURE: TechExposureResponse = {
  tech_exposure: [
    { name: 'nginx', version: '1.18.0', other_asset_count: 34 },
    { name: 'OpenSSH', version: '7.4', other_asset_count: 12 },
    { name: 'WordPress', version: '5.4', other_asset_count: 0 },
    { name: 'PHP', version: null, other_asset_count: 5 },
    { name: 'jQuery', version: '1.12.4', other_asset_count: 61 },
  ],
}
